import React, { useEffect, useState } from 'react';
import { Star, MessageSquare } from 'lucide-react';
import { Place, PlaceReview } from '../../../types/results.types';
import api from '../../../services/api';

interface ReviewsTabProps {
  place: Place;
  searchId?: number;
}

const formatReviewStrength = (value?: string | null): string => {
  if (!value || value === 'none') return 'None';
  if (value === 'light') return 'Light';
  if (value === 'established') return 'Established';
  return value;
};

const formatPercent = (value?: number | null): string => {
  if (value === null || value === undefined) return '—';
  return `${Math.round(value * 100)}%`;
};

const RatingStars: React.FC<{ rating: number; size?: number }> = ({ rating, size = 14 }) => {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  const starPath = 'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.173 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z';
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(full)].map((_, i) => (
        <svg key={`f${i}`} width={size} height={size} viewBox="0 0 20 20" fill="#F59E0B"><path d={starPath} /></svg>
      ))}
      {half && (
        <div className="relative" style={{ width: size, height: size }}>
          <svg width={size} height={size} viewBox="0 0 20 20" fill="#D1D5DB" className="absolute"><path d={starPath} /></svg>
          <div className="absolute inset-0 overflow-hidden" style={{ width: size / 2 }}>
            <svg width={size} height={size} viewBox="0 0 20 20" fill="#F59E0B"><path d={starPath} /></svg>
          </div>
        </div>
      )}
      {[...Array(empty)].map((_, i) => (
        <svg key={`e${i}`} width={size} height={size} viewBox="0 0 20 20" fill="#D1D5DB"><path d={starPath} /></svg>
      ))}
    </div>
  );
};

const ReviewCard: React.FC<{ review: PlaceReview }> = ({ review }) => (
  <div className="rounded-lg border border-gray-100 bg-white p-3 space-y-2">
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm font-semibold text-[#2D3142] truncate">
        {review.reviewer_name ?? 'Anonymous'}
      </span>
      <div className="flex items-center gap-2 flex-shrink-0">
        {review.rating != null && <RatingStars rating={review.rating} size={12} />}
        {review.relative_time && (
          <span className="text-[10px] text-gray-400">{review.relative_time}</span>
        )}
      </div>
    </div>
    {review.text && (
      <p className="text-xs text-gray-600 leading-relaxed">{review.text}</p>
    )}
    {review.owner_response_text && (
      <div className="ml-3 pl-3 border-l-2 border-[#8A3033]/30">
        <p className="text-[10px] font-semibold text-[#8A3033] mb-1">
          Owner reply {review.owner_response_relative_time ? `· ${review.owner_response_relative_time}` : ''}
        </p>
        <p className="text-xs text-gray-600 leading-relaxed">{review.owner_response_text}</p>
      </div>
    )}
  </div>
);

const ReviewsTab: React.FC<ReviewsTabProps> = ({ place, searchId }) => {
  const [reviews, setReviews] = useState<PlaceReview[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!searchId || !place.id) return;
    const controller = new AbortController();
    setLoading(true);
    api.get<PlaceReview[]>(`/api/searches/${searchId}/places/${place.id}/reviews`, { signal: controller.signal })
      .then(res => setReviews(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [searchId, place.id]);

  const leadSignals = place.lead_signals;
  const hasRating = place.review_rating && place.review_rating > 0;

  if (!hasRating && !place.review_count) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Star className="w-10 h-10 text-gray-300 mb-3" />
        <p className="text-sm font-semibold text-gray-500">No Google reviews yet</p>
        <p className="text-xs text-gray-400 mt-1">This business hasn't received any Google reviews.</p>
      </div>
    );
  }

  const high = leadSignals?.visible_high_rating_review_count ?? 0;
  const low = leadSignals?.visible_low_rating_review_count ?? 0;
  const scraped = leadSignals?.visible_reviews_scraped_count ?? 0;
  const mid = Math.max(0, scraped - high - low);
  const totalScraped = high + mid + low;
  const showBars = totalScraped > 0;
  const barPct = (n: number) => Math.round((n / totalScraped) * 100);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-5 p-4 rounded-xl border border-amber-200 bg-amber-50">
        <div className="text-center flex-shrink-0">
          <div className="text-4xl font-extrabold text-amber-800 leading-none tracking-tight">
            {place.review_rating?.toFixed(1)}
          </div>
          <div className="mt-1.5">
            {place.review_rating && <RatingStars rating={place.review_rating} size={13} />}
          </div>
          <div className="text-xs font-semibold text-amber-700 mt-1.5">{place.review_count} reviews</div>
        </div>

        {showBars && (
          <div className="flex-1 space-y-1.5">
            {[
              { label: '4-5★', count: high, pct: barPct(high) },
              { label: '3★',   count: mid,  pct: barPct(mid) },
              { label: '1-2★', count: low,  pct: barPct(low) },
            ].map(row => (
              <div key={row.label} className="flex items-center gap-2 text-xs text-amber-800">
                <span className="w-8 text-right font-medium flex-shrink-0">{row.label}</span>
                <div className="flex-1 h-1.5 bg-amber-200/60 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${row.pct}%` }} />
                </div>
                <span className="w-8 font-semibold flex-shrink-0">{row.pct}%</span>
              </div>
            ))}
            <p className="text-[10px] text-amber-600 mt-1">Based on the last {totalScraped} reviews</p>
          </div>
        )}
      </div>

      {leadSignals && (
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: 'Response rate',   value: formatPercent(leadSignals.visible_owner_response_rate) },
            { label: 'Review photos',   value: leadSignals.visible_review_photo_count != null ? String(leadSignals.visible_review_photo_count) : '—' },
            { label: 'Days since last', value: leadSignals.visible_latest_review_age_days != null ? `${leadSignals.visible_latest_review_age_days}d` : '—' },
            { label: 'Review strength', value: formatReviewStrength(leadSignals.review_strength) },
          ].map(tile => (
            <div key={tile.label} className="rounded-lg border border-[#F9F5F5] bg-white p-3">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{tile.label}</p>
              <p className="text-sm font-bold text-[#2D3142]">{tile.value}</p>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-6 text-gray-400">
          <MessageSquare className="w-4 h-4 animate-pulse mr-2" />
          <span className="text-xs">Loading reviews…</span>
        </div>
      )}
      {!loading && reviews.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Scraped reviews</p>
          {reviews.map(r => <ReviewCard key={r.id} review={r} />)}
        </div>
      )}
    </div>
  );
};

export default ReviewsTab;
